/**
 * Busca global (Ctrl/⌘+K): clientes por nome ou telefone e trechos de
 * mensagens, de qualquer tela. Setas + Enter abrem a conversa.
 */
import type { ResultadoBusca } from "@ydchat/shared";
import { formatarTelefone } from "@ydchat/shared";
import { useNavigate } from "@tanstack/react-router";
import { Loader2, MessageSquareText, Search, X } from "lucide-react";
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Avatar } from "@/components/ui/avatar";
import { Selo } from "@/components/ui/selo";
import { useBusca } from "@/lib/central";
import { cn } from "@/lib/cn";
import { horaOuData } from "@/lib/formatar";

export function useAtalhoBusca(abrir: () => void) {
  useEffect(() => {
    const tecla = (e: globalThis.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        abrir();
      }
    };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, [abrir]);
}

export function BuscaGlobal({ aberta, onFechar }: { aberta: boolean; onFechar: () => void }) {
  const navegar = useNavigate();
  const [q, setQ] = useState("");
  const [aplicada, setAplicada] = useState("");
  const [indice, setIndice] = useState(0);
  const campo = useRef<HTMLInputElement>(null);
  const busca = useBusca(aplicada);

  useEffect(() => {
    const t = setTimeout(() => setAplicada(q.trim()), 200);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    if (aberta) {
      setQ("");
      setIndice(0);
      setTimeout(() => campo.current?.focus(), 0);
    }
  }, [aberta]);
  useEffect(() => setIndice(0), [aplicada]);

  if (!aberta) return null;
  const resultados = busca.data ?? [];

  const abrir = (r: ResultadoBusca) => {
    if (!r.conversa) return;
    onFechar();
    void navegar({
      to: "/atendimentos/$conversaId",
      params: { conversaId: r.conversa.id },
      search: { escopo: "todas" } as never,
    });
  };

  const tecla = (e: KeyboardEvent) => {
    if (e.key === "Escape") onFechar();
    if (!resultados.length) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setIndice(
        (i) => (i + (e.key === "ArrowDown" ? 1 : -1) + resultados.length) % resultados.length,
      );
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const r = resultados[indice];
      if (r) abrir(r);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-3 pt-[10vh]"
      onKeyDown={tecla}
    >
      <button
        aria-label="Fechar busca"
        className="absolute inset-0 bg-[rgba(28,24,21,0.45)]"
        onClick={onFechar}
      />
      <div
        role="dialog"
        aria-label="Buscar"
        className="relative w-full max-w-xl overflow-hidden rounded-xl bg-white shadow-lg"
      >
        <div className="flex items-center gap-2 border-b border-neutral-200 px-4">
          <Search size={18} strokeWidth={1.75} className="text-neutral-400" />
          <input
            ref={campo}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar cliente, telefone ou mensagem"
            aria-label="Buscar"
            className="h-14 flex-1 bg-transparent text-[15px] outline-none placeholder:text-neutral-400 focus-visible:shadow-none"
          />
          {busca.isFetching && <Loader2 size={16} className="animate-spin text-neutral-400" />}
          <button
            onClick={onFechar}
            aria-label="Fechar"
            className="flex size-8 items-center justify-center rounded-md text-neutral-500 hover:bg-neutral-100"
          >
            <X size={16} strokeWidth={1.75} />
          </button>
        </div>
        <ul role="listbox" className="rolagem-fina max-h-[60vh] overflow-y-auto p-1.5">
          {aplicada.length < 2 ? (
            <li className="px-3 py-6 text-center text-sm text-neutral-500">
              Digite pelo menos 2 letras. Atalho: Ctrl+K (⌘K no Mac).
            </li>
          ) : !busca.isPending && !resultados.length ? (
            <li className="px-3 py-6 text-center text-sm text-neutral-500">
              Nada encontrado para “{aplicada}”.
            </li>
          ) : (
            resultados.map((r, i) => (
              <li
                key={`${r.contatoId}-${r.conversa?.id ?? "x"}-${i}`}
                role="option"
                aria-selected={i === indice}
              >
                <button
                  onClick={() => abrir(r)}
                  onMouseEnter={() => setIndice(i)}
                  disabled={!r.conversa}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left disabled:cursor-default",
                    i === indice && "bg-gold-50",
                  )}
                >
                  {r.trecho && r.conversa && !r.trecho.startsWith("Em atendimento") ? (
                    <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-500">
                      <MessageSquareText size={18} strokeWidth={1.75} />
                    </span>
                  ) : (
                    <Avatar nome={r.nome} />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="truncate font-semibold text-neutral-900">{r.nome}</span>
                      {!r.erpClienteId && <Selo tom="dourado">Novo cliente</Selo>}
                    </span>
                    <span className="block truncate text-[13px] text-neutral-500">
                      {r.trecho ?? formatarTelefone(r.telefone)}
                    </span>
                  </span>
                  {r.conversa && (
                    <span className="shrink-0 text-right text-[11px] text-neutral-500">
                      {r.conversa.status === "encerrada" ? "Concluída" : "Aberta"}
                      {r.conversa.ultimaMensagemEm && (
                        <span className="block">{horaOuData(r.conversa.ultimaMensagemEm)}</span>
                      )}
                    </span>
                  )}
                </button>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}
